// TelcoTrack — cassette-rack occupancy connections (SSOT with RSU weld/patch/splitter-path).
// Endpoint IDs are stable; panel/open order never implies direction.

export const OCCUPANCY_CONNECTION_TYPES = ['weld', 'patchcord', 'splitter-path'] as const;
export type OccupancyConnectionType = (typeof OCCUPANCY_CONNECTION_TYPES)[number];

export const OCCUPANCY_ENDPOINT_KINDS = [
  'cable-fiber',
  'cassette-connector',
  'splitter-in',
  'splitter-out',
] as const;
export type OccupancyEndpointKind = (typeof OCCUPANCY_ENDPOINT_KINDS)[number];

export const OCCUPANCY_CABLE_ROLES = ['incoming', 'outgoing'] as const;
export type OccupancyCableRole = (typeof OCCUPANCY_CABLE_ROLES)[number];

export type OccupancyCableFiberEndpoint = {
  kind: 'cable-fiber';
  cableId: string;
  physicalFiber: number;
  role: OccupancyCableRole | null;
};

export const OCCUPANCY_CONNECTOR_SIDES = ['splice', 'patch'] as const;
export type OccupancyConnectorSide = (typeof OCCUPANCY_CONNECTOR_SIDES)[number];
export type OccupancyConnectorSideName = 'splice-side' | 'patch-side';

export type OccupancyCassetteConnectorEndpoint = {
  kind: 'cassette-connector';
  cassetteId: string;
  slotInCassette: number;
  /** Omitted / null = legacy unsided `cas:{id}:conn:{slot}`. */
  side?: OccupancyConnectorSide | null;
};

/** One optical channel through an adapter (LC a/b are two channels, not sides). */
export type OccupancyConnectorChannel = {
  id: string;
  cassetteId: string;
  adapterPosition: number;
  channel: 'a' | 'b' | null;
  connectorType: 'SC/APC' | 'LC/APC';
  spliceSideEndpointId: string;
  patchSideEndpointId: string;
};

export type OccupancySplitterInEndpoint = {
  kind: 'splitter-in';
  cassetteId: string;
  splitterIndex: number;
};

export type OccupancySplitterOutEndpoint = {
  kind: 'splitter-out';
  cassetteId: string;
  splitterIndex: number;
  output: number;
};

export type OccupancyEndpoint =
  | OccupancyCableFiberEndpoint
  | OccupancyCassetteConnectorEndpoint
  | OccupancySplitterInEndpoint
  | OccupancySplitterOutEndpoint;

/** Inclusive physical-fiber range stored on a range assign before expansion. */
export type OccupancyFiberRange = {
  from: number;
  to: number;
};

/** Fiber ↔ fiber, fiber ↔ pigtail, or fiber ↔ unterminated splitter IN/OUT. */
export type OccupancyWeldConnection = {
  id: string;
  fromEndpointId: string;
  toEndpointId: string;
  /** Range UI (12 fibers → expand to 12 rows). Ribbon keeps one row + both counts. */
  fiberRange?: OccupancyFiberRange;
  physicalWeldOperationCount?: number;
  splicedFiberCount?: number;
};

/** Patch-side patchcord: connector ↔ splitter IN/OUT (or another connector). */
export type OccupancyPatchConnection = {
  id: string;
  fromEndpointId: string;
  toEndpointId: string;
};

export type OccupancySplitterPath = {
  id: string;
  cassetteId: string;
  splitterIndex: number;
  inputPatchId: string | null;
  outputPatches: Array<{ output: number; patchId: string | null }>;
};

export const OCCUPANCY_CONNECTION_ISSUE_CODES = [
  'ORIENTATION_NEEDS_CABLE_ROLE',
  'CONNECTION_DIRECTION_UNKNOWN',
  'PATCH_PORT_OCCUPIED',
  'WELD_PORT_OCCUPIED',
  'WELD_FIBER_OCCUPIED',
  'DIRECT_CABLE_CABLE_FORBIDDEN',
  'SPLITTER_OUT_OCCUPIED',
  'CONNECTOR_TYPE_INCOMPATIBLE',
  'SPLITTER_OUT_USED_AS_IN',
  'OUTGOING_AS_PATH_START',
  'SPLITTER_PATH_INCOMPLETE',
  'SPLITTER_PATH_CYCLIC',
  'ENDPOINT_UNKNOWN',
] as const;
export type OccupancyConnectionIssueCode = (typeof OCCUPANCY_CONNECTION_ISSUE_CODES)[number];

export type OccupancyConnectionIssue = {
  code: OccupancyConnectionIssueCode;
  path: string;
  message: string;
  endpointId?: string;
};

/** Walk from incoming fiber; SC/LC order is not assumed. */
export type OccupancyServicePath = {
  id: string;
  incomingEndpointId: string | null;
  scEndpointId: string | null;
  splitterInEndpointId: string | null;
  splitterOutEndpointId: string | null;
  lcEndpointId: string | null;
  outgoingEndpointId: string | null;
  weldIds: string[];
  patchIds: string[];
  complete: boolean;
};
